import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../../commons/Auth';
import { useTranslation } from 'react-i18next';
import { useLoading } from '../../../commons/Loading';
import { useAlert } from 'react-alert';
import {
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from 'use-query-params';
import insurancesAPIs from '../../../lib/fetch/insurancesAPIs';
import InsurancesPresentational from './InsurancesPresentational';
import moment from 'moment';
const { REACT_APP_DEFAULT_PAGINATION_LIMIT } = process.env;

const Insurances = ({ profile = {} }) => {
  const [{ newToken } = {}] = useAuth();
  const [t] = useTranslation();
  const { showLoading, hideLoading } = useLoading();
  const alert = useAlert();

  const [insurances, setInsurances] = useState([]);
  const [total, setTotal] = useState(0);

  const [selectedInsurances, setSelectedInsurances] = useState([]);
  const [checkedRows, setCheckedRows] = useState([]);
  const [selectedAll, setSelectedAll] = useState(false);

  const [condos, setCondos] = useState([]);
  const [customers, setCustomers] = useState([]);

  const [query, setQuery] = useQueryParams({
    limit: withDefault(NumberParam, +REACT_APP_DEFAULT_PAGINATION_LIMIT),
    offset: NumberParam,
    insuranceStartYear: NumberParam,
    insuranceStartMonth: NumberParam,
    insuranceEndYear: NumberParam,
    insuranceEndMonth: NumberParam,
    policy_code: withDefault(StringParam, ''),
    admin_cf: withDefault(StringParam, ''),
    supplier: withDefault(StringParam, ''),
    customer: withDefault(StringParam, ''),
    customerType: withDefault(StringParam, ''),
    active: withDefault(StringParam, ''),
    agencyType: withDefault(StringParam, ''),
    renovated: withDefault(StringParam, ''),
  });

  const {
    limit,
    offset,
    insuranceStartYear,
    insuranceStartMonth,
    insuranceEndYear,
    insuranceEndMonth,
    policy_code,
    admin_cf,
    supplier,
    customer,
    customerType,
    active,
    agencyType,
    renovated,
  } = query;

  function onFilter(queryDiff) {
    if (
      (queryDiff.insuranceStartYear &&
        queryDiff.insuranceStartYear !== insuranceStartYear) || // first search
      (insuranceStartYear && queryDiff.insuranceStartYear === '') || // search deleted
      (queryDiff.insuranceStartMonth &&
        queryDiff.insuranceStartMonth !== insuranceStartMonth) || // first search
      (insuranceStartMonth && queryDiff.insuranceStartMonth === '') || // search deleted
      (queryDiff.insuranceEndYear &&
        queryDiff.insuranceEndYear !== insuranceEndYear) || // first search
      (insuranceEndYear && queryDiff.insuranceEndYear === '') || // search deleted
      (queryDiff.insuranceEndMonth &&
        queryDiff.insuranceEndMonth !== insuranceEndMonth) || // first search
      (insuranceEndMonth && queryDiff.insuranceEndMonth === '') ||
      (queryDiff.policy_code && queryDiff.policy_code !== policy_code) || // first search
      (policy_code && queryDiff.policy_code === '') || // search deleted
      (queryDiff.agencyType && queryDiff.agencyType !== agencyType) || // first search
      (agencyType && queryDiff.agencyType === '') || // search deleted
      (queryDiff.admin_cf && queryDiff.admin_cf !== admin_cf) || // first search
      (admin_cf && queryDiff.admin_cf === '') || // search deleted
      (queryDiff.supplier && queryDiff.supplier !== supplier) || // first search
      (supplier && queryDiff.supplier === '') || // search deleted
      (queryDiff.customer && queryDiff.customer !== customer) || // first search
      (customer && queryDiff.customer === '') || // search deleted
      (queryDiff.customerType && queryDiff.customerType !== customerType) || // first search
      (customerType && queryDiff.customerType === '') || // search deleted
      (queryDiff.active && queryDiff.active !== active) || // first search
      (active && queryDiff.active === '') || // search deleted
      (queryDiff.renovated && queryDiff.renovated !== renovated) || // first search
      (renovated && queryDiff.renovated === '') || // search deleted
      (queryDiff.limit && queryDiff.limit !== limit)
    ) {
      queryDiff.offset = 0; // return to first page
    }

    setQuery(queryDiff);
  }

  const fetchInsurances = async (query) => {
    showLoading();
    const { data, total: _total } = await insurancesAPIs.findUserInsurances({
      newToken,
      query,
      userId: profile._id,
    });
    hideLoading();

    setTotal(Number(_total || 0));
    setInsurances(data);
  };

  const onSelectChange = (row) => {
    if (selectedInsurances.includes(row._id)) {
      setSelectedAll(false);
      setCheckedRows((checkedRows) =>
        checkedRows.filter(({ _id }) => _id !== row._id)
      );
      return setSelectedInsurances((selectedInsurances) =>
        selectedInsurances.filter((insuranceId) => insuranceId !== row._id)
      );
    }
    setSelectedInsurances((selectedInsurances) => [
      ...selectedInsurances,
      row._id,
    ]);
    setCheckedRows((checkedRows) => [...checkedRows, row]);
  };

  const onSelectAll = (selectAll) => {
    setSelectedAll(selectAll);
    if (!selectAll) {
      setCheckedRows([]);
      return setSelectedInsurances([]);
    }
    setCheckedRows(
      insurances.filter((insurance) => insurance.pinvInvoice.length === 0)
    );
    setSelectedInsurances(
      insurances
        .filter((insurance) => insurance.pinvInvoice.length === 0)
        .map(({ _id }) => _id)
    );
  };

  const onInvoice = async () => {
    showLoading();

    const startDateYear = moment(checkedRows[0]?.start_date).year();

    for (const row of checkedRows) {
      const rowYear = moment(row.start_date).year();
      if (rowYear !== startDateYear) {
        alert.error(
          'Le assicurazioni selezionate presentano anni di inizio differenti.'
        );
        hideLoading();
        return;
      }
    }

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/panoramica-invoices/${profile._id}/insurances`,
      {
        selectedInsurances,
      },
      {
        headers: { Authorization: newToken },
      }
    );
    hideLoading();

    if (response instanceof Error) {
      return alert.error(t('COMMON:GENERIC_ERROR'));
    }

    alert.success(t('COMMON:SUCCESS_UPDATE'));

    fetchInsurances(query);
    setSelectedInsurances([]);
    setSelectedAll(false);
  };

  const searchAdministrators = async (value) => {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/profiles/admins`,
      {
        headers: { Authorization: newToken },
        params: { search: value },
      }
    );

    if (response instanceof Error) {
      alert.error(t('COMMON:GENERIC_ERROR'));
      return [];
    }

    const { data = [] } = response;
    return data;
  };

  const searchEstateAgents = async (value) => {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/profiles/estateAgents`,
      {
        headers: { Authorization: newToken },
        params: { search: value },
      }
    );

    if (response instanceof Error) {
      alert.error(t('COMMON:GENERIC_ERROR'));
      return [];
    }

    const { data = [] } = response;
    return data;
  };

  const searchCommercials = async (value) => {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/profiles/commercials`,
      {
        headers: { Authorization: newToken },
        params: { search: value },
      }
    );

    if (response instanceof Error) {
      alert.error(t('COMMON:GENERIC_ERROR'));
      return [];
    }

    const { data = [] } = response;
    return data;
  };

  const fetchAdminCondos = async (adminId, start_date) => {
    if (!adminId) {
      setCondos([]);
      return;
    }

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/condos/insurances/${adminId}`,
      {
        params: {
          limit: 500,
          start_date,
        },
        headers: { Authorization: newToken },
      }
    );

    if (response instanceof Error) {
      alert.error(t('COMMON:GENERIC_ERROR'));
      return [];
    }

    const { data = [] } = response;

    setCondos(data.condos);
  };

  const fetchAdminCustomers = async (adminId) => {
    if (!adminId) {
      setCustomers([]);
      return;
    }

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/customer-extra?profileId=${adminId}`,
      {
        params: {
          limit: 500,
        },
        headers: { Authorization: newToken },
      }
    );

    if (response instanceof Error) {
      alert.error(t('COMMON:GENERIC_ERROR'));
      return [];
    }

    const { data = [] } = response;
    console.log('Customers searched:', data);
    setCustomers(data);
  };

  const onInsuranceUpdate = async (data) => {
    const { _id } = data;
    if (data.admin === '' && data.estateAgent !== '') {
      data.admin = data.estateAgent;
    } else if (data.admin === '' && data.commercial !== '') {
      data.admin = data.commercial;
    }
    delete data.estateAgent;
    delete data.commercial;
    showLoading();

    const response = await axios.put(
      `${process.env.REACT_APP_API_URL}/insurances/${_id}`,
      data,
      {
        headers: { Authorization: newToken },
      }
    );
    hideLoading();

    if (response instanceof Error) {
      const capsulateError = { error: response };
      const { error } = capsulateError;
      const { response: errorResponse, message: errorMessage } = error;
      const { data: errorData } = errorResponse || {};
      const { message: dataMessage } = errorData || {};
      alert.error(dataMessage || errorMessage || t('COMMON:GENERIC_ERROR'));
      return hideLoading();
    }

    alert.success(t('COMMON:SUCCESS_UPDATE'));
    fetchInsurances();
  };

  useEffect(() => {
    if (profile._id) {
      fetchInsurances(query);
    }
  }, [
    limit,
    offset,
    insuranceStartYear,
    insuranceStartMonth,
    insuranceEndYear,
    insuranceEndMonth,
    policy_code,
    admin_cf,
    supplier,
    customer,
    customerType,
    active,
    agencyType,
    renovated,
  ]);

  return (
    <InsurancesPresentational
      data={insurances}
      profile={profile}
      query={query}
      total={total}
      onFilter={onFilter}
      selectedInsurances={selectedInsurances}
      onInvoice={onInvoice}
      selectedAll={selectedAll}
      onSelectAll={onSelectAll}
      onSelectChange={onSelectChange}
      onStatusChanged={() => fetchInsurances()}
      searchAdministrators={searchAdministrators}
      searchEstateAgents={searchEstateAgents}
      searchCommercials={searchCommercials}
      fetchAdminCondos={fetchAdminCondos}
      fetchAdminCustomers={fetchAdminCustomers}
      condos={condos}
      customersExtra={customers}
      onInsuranceUpdate={onInsuranceUpdate}
    />
  );
};

export default Insurances;
